import { Color3, Mesh, MeshBuilder, Nullable, Observer, StandardMaterial, Texture, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { createEntityFactory } from '@modules/standard';
import * as dateFns from 'date-fns';
import { World } from 'miniplex';
import * as SunCalc from 'suncalc';

import { Entity as BaseEntity } from '..';
import { LightManager, onSeasonChanged, onTimeIsRunning } from '../..';
import sunTexture from '../../assets/materials/textures/sun.png';
import { LayerMask } from '../../Constants';

export enum Season {
  Spring = 0,
  Summer = 1,
  Fall = 2,
  Winter = 3,
}

export type SunEntity = {
  SunOptions?: {
    date: Date;
  };
  SunPosition?: {
    date: Date;
    altitude: number;
    azimuth: number;
  };
};

type Entity = SunEntity & BaseEntity;

/**
 * ## Known issues
 *
 * - accuracy https://github.com/mourner/suncalc/issues/101
 * - use meeus or vsop87 like `astronomy-bundle` or `aa-js` etc.
 */
export class SunSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('SunSystem');

  private readonly withOptions;
  private readonly withGround;
  private readonly withSunPosition;
  private readonly withSkybox;
  private readonly scene;
  private lastVersions = {
    options: -Infinity,
    ground: -Infinity,
    position: -Infinity,
  };
  private readonly createEntity;

  private readonly lightManager;

  private readonly options = {
    distance: 149_597_870_577.28, // 1 AU (for light direction calculation)
    sunBillboardDistance: 500, // Distance from camera to draw sun billboard
    sunBillboardSize: 40, // Visual size of sun billboard
  };

  private onSeasonChanged: Nullable<Observer<Season>>;
  private onTimeIsRunning: Nullable<Observer<boolean>>;
  private currentDate: Date;
  private isTimeRunning: boolean = false;
  private sunSphere: Mesh | undefined;

  private readonly seasonDate = {
    [Season.Spring]: {
      start: dateFns.set(new Date(), { month: 2, date: 1, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
      end: dateFns.set(new Date(), { month: 4, date: 30, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
    },
    [Season.Summer]: {
      start: dateFns.set(new Date(), { month: 5, date: 1, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
      end: dateFns.set(new Date(), { month: 7, date: 31, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
    },
    [Season.Fall]: {
      start: dateFns.set(new Date(), { month: 8, date: 1, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
      end: dateFns.set(new Date(), { month: 9, date: 31, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
    },
    [Season.Winter]: {
      start: dateFns.set(new Date(), { month: 10, date: 1, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
      end: dateFns.set(new Date(), { month: 1, date: 29, hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }),
    },
  };

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [LightManager.INJECTABLE_TOKEN]: lightManager,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [LightManager.INJECTABLE_TOKEN]: LightManager;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    // Billboard sprite for sun (always faces camera)
    // Use fixed visual size instead of real angular size for better visibility
    this.sunSphere = MeshBuilder.CreatePlane('SunBillboard', {
      size: this.options.sunBillboardSize,
      sideOrientation: Mesh.FRONTSIDE,
    });
    this.sunSphere.billboardMode = Mesh.BILLBOARDMODE_ALL; // Always face camera
    this.sunSphere.layerMask = LayerMask.Sky;
    this.sunSphere.isPickable = false;
    this.sunSphere.ignoreCameraMaxZ = true;
    this.sunSphere.renderingGroupId = 1; // Render after sky background
    this.sunSphere.position.set(0, 0, 0);

    // Create sun material with texture
    const sunMaterial = new StandardMaterial('SunMaterial', babylonjs.scene);
    sunMaterial.emissiveColor = new Color3(1, 1, 1); // White to show texture color
    sunMaterial.emissiveTexture = new Texture(sunTexture, babylonjs.scene);
    sunMaterial.disableLighting = true; // Don't need lighting, it's self-illuminated
    sunMaterial.backFaceCulling = false;
    sunMaterial.opacityTexture = sunMaterial.emissiveTexture; // Use same texture for alpha
    sunMaterial.useAlphaFromDiffuseTexture = true;
    this.sunSphere.material = sunMaterial;

    babylonjs.scene.addMesh(this.sunSphere);

    this.withOptions = world.with('SunOptions');
    this.withGround = world.with('Ground');
    this.withSunPosition = world.with('SunPosition', 'GUIDisplayable');
    this.withSkybox = world.with('Skybox', 'TransformNode');
    this.scene = babylonjs.scene;
    this.createEntity = createEntityFactory(world);
    this.onSeasonChanged = onSeasonChanged.add((season) => {
      this.whenUpdateDate(season);
    });
    this.onTimeIsRunning = onTimeIsRunning.add((isRunning) => {
      this.isTimeRunning = isRunning;
    });

    this.createEntity({
      SunOptions: {
        date: this.seasonDate[Season.Spring].start,
      },
    });
    this.currentDate = this.seasonDate[Season.Spring].start;
    this.lightManager = lightManager;
  }

  whenUpdateDate = (date: Season) => {
    this.currentDate = this.seasonDate[date].start;
    this.withOptions.clear();
    this.createEntity({
      SunOptions: {
        date: this.seasonDate[date].start,
      },
    });
  };

  whenUpdateTime = () => {
    if (!this.isTimeRunning) return;
    const Options = this.withOptions.first?.SunOptions;
    const Ground = this.withGround.first?.Ground;

    if (!Ground || !Options) return;

    let newTime = dateFns.addMinutes(this.currentDate, 0.1 * this.scene.deltaTime);
    const { sunrise, sunset } = SunCalc.getTimes(Options.date, Ground.latitude, Ground.longitude);
    if (dateFns.isBefore(newTime, sunrise)) {
      newTime = sunrise;
    } else if (dateFns.isAfter(newTime, sunset)) {
      newTime = sunrise;
    }
    this.currentDate = newTime;
    this.isTimeRunning = true;
  };

  whenBeforeRender = () => {
    const isOptionsUnchanged = this.withOptions.version === this.lastVersions.options;
    const isGroundUnchanged = this.withGround.version === this.lastVersions.ground;
    if (isOptionsUnchanged && isGroundUnchanged) return;

    const Options = this.withOptions.first?.SunOptions;
    const Ground = this.withGround.first?.Ground;
    if (!Ground || !Options) return;

    this.withSunPosition.clear();

    const { sunrise, sunset } = SunCalc.getTimes(Options.date, Ground.latitude, Ground.longitude);

    const from = dateFns.roundToNearestHours(sunrise, { roundingMethod: 'ceil' });
    const to = dateFns.roundToNearestHours(sunset, { roundingMethod: 'floor' });

    for (let time = from; time <= to; time = dateFns.addHours(time, 1)) {
      const { altitude, azimuth } = SunCalc.getPosition(time, Ground.latitude, Ground.longitude);
      const radius = this.options.distance;

      const x = -radius * Math.cos(altitude) * Math.sin(azimuth);
      const y = radius * Math.sin(altitude);
      const z = -radius * Math.cos(altitude) * Math.cos(azimuth);

      this.createEntity({
        SunPosition: {
          date: time,
          altitude,
          azimuth,
        },
        GUIDisplayable: {
          position: new Vector3(x, y, z),
          clip: {
            minX: false,
            maxX: false,
            minY: false,
            maxY: false,
          },
        },
      });
    }

    this.lastVersions.options = this.withOptions.version;
    this.lastVersions.ground = this.withGround.version;
  };

  private whenSunPosition = (force: boolean = false) => {
    const isPositionUnchanged = this.withSunPosition.version === this.lastVersions.position;
    if (isPositionUnchanged && !this.isTimeRunning && !force) return;

    const sunPositions = this.withSunPosition.entities.length;
    if (sunPositions < 1) return;

    const Options = this.withOptions.first?.SunOptions;
    const Ground = this.withGround.first?.Ground;
    if (!Ground || !Options) return;

    const { altitude, azimuth } = SunCalc.getPosition(this.currentDate, Ground.latitude, Ground.longitude);

    // Calculate light direction using real distance (1 AU)
    const realRadius = this.options.distance;
    const lightX = -realRadius * Math.cos(altitude) * Math.sin(azimuth);
    const lightY = realRadius * Math.sin(altitude);
    const lightZ = -realRadius * Math.cos(altitude) * Math.cos(azimuth);

    this.updateSky(altitude, azimuth);
    this.updateLight(new Vector3(lightX, lightY, lightZ));

    // Position sun billboard at fixed distance from camera in sun's direction
    const camera = this.scene.activeCamera;
    if (camera && this.sunSphere) {
      const billboardRadius = this.options.sunBillboardDistance;
      const sunX = -billboardRadius * Math.cos(altitude) * Math.sin(azimuth);
      const sunY = billboardRadius * Math.sin(altitude);
      const sunZ = -billboardRadius * Math.cos(altitude) * Math.cos(azimuth);

      // Position relative to camera
      this.sunSphere.position.copyFrom(camera.position).addInPlace(new Vector3(sunX, sunY, sunZ));
    }

    this.lastVersions.position = this.withSunPosition.version;
  };

  private updateSky = (altitude: number, azimuth: number) => {
    const skybox = this.withSkybox.first?.Skybox;
    if (!skybox) return;
  };

  private updateLight = (position: Vector3) => {
    this.lightManager.mainLight.direction = position.negate().normalize();
    this.lightManager.subLight.direction = position.negate().normalize();
  };

  public getCurrentDate() {
    return this.currentDate;
  }

  dispose() {
    onSeasonChanged.remove(this.onSeasonChanged);
    onTimeIsRunning.remove(this.onTimeIsRunning);
    this.onSeasonChanged = null;
    this.onTimeIsRunning = null;
  }

  operations: SystemOperationDefinition[] = [
    {
      group: 'beforeRender',
      run: this.whenBeforeRender,
    },
    {
      group: 'beforeRender',
      run: this.whenSunPosition,
    },
    {
      group: 'beforeRender',
      run: this.whenUpdateTime,
    },
  ];
}
